import { Named, Owned } from './named';
import { UniqueArray } from './uniquearray';

/*
    Named Array which only allows unique items. This is a fallback for a Set
*/
export class NamedArray<T extends Named> extends UniqueArray<T> {

    private owner: Owned | undefined;
    private onRemove: ((...t: T[]) => boolean) | undefined;

  /**
   * Remove callback when entries are removed from this array. Can choose
   * to disable removing certain items
   */
  constructor(owner?: Owned, onRemove?: (...t: T[]) => boolean, ...values: T[]) {
    super(...values);
    this.owner = owner;
    this.onRemove = onRemove;
    Object.setPrototypeOf(this, NamedArray.prototype);
  }

  public static create<T extends Named>(owner?: Owned, onRemove?: (...t: T[]) => boolean, ...values: T[]): NamedArray<T> {
    return new NamedArray(owner, onRemove, ...values);
  }

  public push(...items: T[]): number {
    const pushed = super.push(...items);
    for (const item of items) {
      item['_owner'] = this.owner;
    }
    return pushed;
  }

  public remove(item: T): void {
    this.canRemove(item);
    super.remove(item);
    item['_owner'] = undefined;
  }

  public pop(): T | undefined {
    this.canRemove(this[length - 1]);
    const t = super.pop();
    if (t) {
        t['_owner'] = undefined;
    }
    return t;
  }

  public shift(): T | undefined {
    this.canRemove(this[length - 1]);
    const t = super.shift();
    if (t) {
        t['_owner'] = undefined;
    }
    return t;
  }

   private canRemove(...items: T[]): void {
    if (this.onRemove && !this.onRemove(...items)) {
      throw new Error('Item cannot be removed');
    }
  }
}
